import { useState } from "react";
import TypeWriterText from "../../components/TypeWriterText";
import { useOutletContext, Link } from "react-router-dom";
import PageHero from "./PageHero";
import { motion, AnimatePresence } from "framer-motion";
import { Award, Heart, Shield, Star, ChevronDown, MapPin, Baby, Users, FileCheck } from "lucide-react";
import KennelSEO, { breadcrumbSchema } from "./KennelSEO";

const timelineItems = [
  {
    icon: MapPin,
    title: "Skąd się wzięliśmy",
    short: "Hodowla powstała z miłości do owczarków niemieckich, rodzinnych wartości i marzenia o miejscu, w którym liczy się jakość, a nie ilość.",
    full: "Nazwa Wolfe Vom Katzenberg nie jest przypadkowa. Mieszkamy na pograniczu Kocich Gór, Krainy Wzgórz Trzebnickich i Doliny Baryczy — regionów wyjątkowych, pięknych i mocno związanych z naturą. To właśnie z tego miejsca narodziła się nazwa naszej hodowli. Jest ona dla nas symbolem zakorzenienia, charakteru i wyjątkowości."
  },
  {
    icon: Heart,
    title: "Nasza filozofia",
    short: "Zależy nam, aby hodowla wyróżniała się wysoką jakością, odpowiedzialnym podejściem i świadomą pracą hodowlaną.",
    full: "Nie idziemy w ilość i nigdy nie chcieliśmy być miejscem, gdzie psy są traktowane jak produkt. Dla nas hodowla to odpowiedzialność za zdrowie, psychikę, eksterier i przyszłość każdego szczenięcia, które przychodzi u nas na świat."
  },
  {
    icon: Star,
    title: "Planowanie miotów",
    short: "Każdy miot planujemy z ogromną starannością. Dobór skojarzeń nie jest u nas przypadkowy.",
    full: "Wybieramy reproduktory bardzo świadomie, również w Niemczech, kierując się nie tylko urodą, ale przede wszystkim jakością linii, ruchem, budową, zdrowiem i zrównoważonym charakterem. Chcemy, aby nasze szczenięta były piękne, mądre, stabilne psychicznie i dobrze przygotowane do życia w rodzinie."
  },
  {
    icon: Users,
    title: "Życie z nami",
    short: "Nasze psy żyją razem z nami, w domu i w codzienności naszej rodziny.",
    full: "Hodowla jest częścią naszego życia, a nie osobnym systemem. Wspólnie spędzamy czas na szkoleniach, wystawach i zwykłych codziennych chwilach. Chcemy też pokazywać naszym dzieciom, że świat oparty na trosce, jakości i miłości do zwierząt naprawdę istnieje."
  },
  {
    icon: Baby,
    title: "Jak wychowujemy szczenięta",
    short: "Szczenięta wychowujemy z wielką starannością — tak, jakby każde miało zostać z nami na zawsze.",
    full: "Nowym właścicielom przekazujemy nie tylko pieska, ale również wsparcie, wiedzę i opiekę. Do każdego szczenięcia dołączamy wyprawkę, kartę informacyjną, zalecenia żywieniowe na pierwszy rok życia. Utrzymujemy stały kontakt z właścicielami i chętnie pomagamy także później."
  },
  {
    icon: FileCheck,
    title: "Standard i kontrola",
    short: "Każdy miot jest kontrolowany i badany przez inspektora weterynaryjnego.",
    full: "Poddajemy się temu dobrowolnie, aby dawać przyszłym właścicielom pełne poczucie bezpieczeństwa. Wolfe Vom Katzenberg to hodowla dla tych, którzy szukają jakości, a nie przypadku — dla tych, którzy rozumieją, że dobry pies zaczyna się od odpowiedzialnych decyzji hodowlanych."
  }
];

const KennelAbout = () => {
  const { settings } = useOutletContext();
  const [openItems, setOpenItems] = useState({});

  const toggle = (i) => setOpenItems(prev => ({ ...prev, [i]: !prev[i] }));

  const values = [
    { icon: Heart, title: "Pasja", desc: "Hodowla to nasza życiowa pasja. Każdy pies jest częścią rodziny." },
    { icon: Shield, title: "Zdrowie", desc: "Pełna diagnostyka zdrowotna. HD, ED, DNA — bez kompromisów." },
    { icon: Award, title: "Jakość", desc: "Hodowla zarejestrowana w ZKwP pod patronatem FCI." },
    { icon: Star, title: "Charakter", desc: "Stawiamy na psy o stabilnym temperamencie i silnym charakterze." },
  ];

  return (
    <div>
      <KennelSEO
        title="O hodowli Wolfe vom Katzenberg | Hodowla Owczarka Niemieckiego"
        description="Poznaj hodowlę Wolfe vom Katzenberg — naszą filozofię, wartości i podejście do hodowli owczarka niemieckiego. Zarejestrowana w ZKwP i FCI."
        canonical="https://wolfevomkatzenberg.pl/o-hodowli"
        schema={[breadcrumbSchema([{ name: "Start", href: "/" }, { name: "O hodowli", href: "/o-hodowli" }])]}
      />

      {/* Hero */}
      <PageHero
        breadcrumbs={[{ label: "O HODOWLI" }]}
        label="O HODOWLI"
        title="Hodowla tworzona z serca i doświadczenia"
        description="Nasza hodowla powstała z miłości do owczarków niemieckich i z potrzeby tworzenia czegoś wartościowego. Każdy pies jest dla nas ważny — znamy ich charaktery, potrzeby i emocje. Spędzamy z nimi codzienność, obserwujemy ich rozwój i dbamy o to, aby miały jak najlepsze warunki do życia. Nie działamy przypadkowo. Każde skojarzenie jest przemyślane, a naszym celem jest tworzenie psów stabilnych, zdrowych i dobrze przygotowanych do życia w nowych domach."
        testId="about"
      />

      {/* Timeline diagram */}
      <section className="bg-[#141416] border-t border-[#2A2A2D] px-6 md:px-12 py-20">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-4">NASZA DROGA</span>
            <h2 className="font-serif text-[clamp(1.6rem,3vw,2.4rem)] text-[#F5F5F5]">Co nas definiuje</h2>
            <div className="w-12 h-px bg-[#D4AF37]/40 mx-auto mt-5" />
          </div>

          <div className="relative">
            {/* Vertical line */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#D4AF37]/30 to-transparent" />

            <div className="space-y-8 md:space-y-0">
              {timelineItems.map((item, i) => {
                const Icon = item.icon;
                const isLeft = i % 2 === 0;
                const isOpen = openItems[i];
                return (
                  <motion.div key={i}
                    initial={{ opacity: 0, x: isLeft ? -30 : 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className={`md:flex items-start md:py-6 ${isLeft ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                    data-testid={`about-timeline-${i}`}
                  >
                    {/* Content card */}
                    <div className={`md:w-[calc(50%-2rem)] ${isLeft ? 'md:text-right md:pr-0' : 'md:text-left md:pl-0'}`}>
                      <div className="bg-[#1A1A1D] border border-[#2A2A2D] p-6 w-full hover:border-[#D4AF37]/30 transition-colors duration-300">
                        <div className={`flex items-start gap-4 ${isLeft ? 'md:flex-row-reverse' : ''}`}>
                          <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                            <Icon className="w-5 h-5 text-[#D4AF37]" />
                          </div>
                          <div className={isLeft ? 'md:text-right' : ''}>
                            <h3 className="text-[#F5F5F5] text-sm font-medium mb-2">{item.title}</h3>
                            <p className="text-[#B8B8B8]/60 text-xs leading-relaxed">{item.short}</p>

                            <AnimatePresence>
                              {isOpen && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: "auto", opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.3 }}
                                  className="overflow-hidden"
                                >
                                  <p className="text-[#B8B8B8]/50 text-xs leading-relaxed mt-3 pt-3 border-t border-[#2A2A2D]">{item.full}</p>
                                </motion.div>
                              )}
                            </AnimatePresence>

                            <button onClick={() => toggle(i)}
                              className="inline-flex items-center gap-1 mt-3 text-[9px] tracking-[0.2em] text-[#D4AF37] hover:text-[#C8A96A] transition-colors"
                              data-testid={`about-toggle-${i}`}
                            >
                              {isOpen ? 'ZWIŃ' : 'CZYTAJ WIĘCEJ'}
                              <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Center dot */}
                    <div className="hidden md:flex w-16 justify-center flex-shrink-0 pt-6">
                      <div className="w-3 h-3 rounded-full bg-[#D4AF37] border-2 border-[#141416] shadow-[0_0_8px_rgba(212,175,55,0.4)]" />
                    </div>

                    {/* Spacer */}
                    <div className="hidden md:block md:w-[calc(50%-2rem)]" />
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#0B0B0D] px-6 md:px-12 py-20" id="wartosci">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-3">NASZE WARTOŚCI</span>
            <h2 className="font-serif text-[clamp(1.5rem,3vw,2.2rem)] text-[#F5F5F5] mb-4">Co nas wyróżnia</h2>
            <div className="w-12 h-px bg-[#D4AF37]/30 mx-auto" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: 0.3 + i * 0.1 }}
                className="bg-[#1A1A1D] border border-[#2A2A2D] p-7 hover:border-[#D4AF37]/20 transition-colors"
                data-testid={`value-card-${i}`}
              >
                <v.icon className="w-6 h-6 text-[#D4AF37] mb-4" />
                <h3 className="text-[#F5F5F5] font-medium mb-2">{v.title}</h3>
                <p className="text-[#B8B8B8]/60 text-sm leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#141416] border-t border-[#2A2A2D] px-6 md:px-12 py-16">
        <div className="max-w-5xl mx-auto text-center">
          <Link to="/kontakt"
            className="px-10 py-3.5 border border-[#D4AF37] text-[#D4AF37] text-xs tracking-[0.15em] hover:bg-[#D4AF37] hover:text-[#0B0B0D] transition-all duration-300 inline-block"
            data-testid="about-contact-cta"
          >
            SKONTAKTUJ SIĘ Z NAMI
          </Link>
        </div>
      </section>
    </div>
  );
};

export default KennelAbout;
